import { Component } from '@theme/component';

/**
 * BagFinderComponent
 * Handles tab-based filtering for the bag finder section.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} filters - The filter buttons container
 * @property {HTMLButtonElement[]} filterBtns - Array of filter buttons
 * @property {HTMLElement} carousel - The slideshow carousel container
 * @property {HTMLElement[]} cards - Array of bag card elements
 *
 * @extends {Component<Refs>}
 */
class BagFinderComponent extends Component {
  /** @type {string} */
  #activeFilter = 'all';

  connectedCallback() {
    super.connectedCallback();
    this.#setupEventListeners();
    this.#setupKlaviyoButtons();
  }

  /**
   * Sets up click event listeners for filter buttons.
   */
  #setupEventListeners() {
    if (!this.refs.filterBtns) return;

    for (const btn of this.refs.filterBtns) {
      btn.addEventListener('click', this.#handleFilterClick);
    }
  }

  /**
   * Sets up Klaviyo notify button functionality.
   */
  #setupKlaviyoButtons() {
    const klaviyoBtns = this.querySelectorAll('.klaviyo-notify-btn');

    for (const btn of klaviyoBtns) {
      btn.addEventListener('click', this.#handleKlaviyoClick);
    }
  }

  /**
   * Handles filter button clicks.
   *
   * @param {Event} event - The click event
   */
  #handleFilterClick = (event) => {
    const target = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const filter = target.dataset.filter;

    if (!filter || filter === this.#activeFilter) return;

    this.#activeFilter = filter;
    this.#updateFilterButtons();
    this.#filterCards();
  };

  /**
   * Updates the aria-pressed state of filter buttons.
   */
  #updateFilterButtons() {
    if (!this.refs.filterBtns) return;

    for (const btn of this.refs.filterBtns) {
      const isActive = btn.dataset.filter === this.#activeFilter;
      btn.setAttribute('aria-pressed', String(isActive));
    }
  }

  /**
   * Filters the bag cards based on the active filter.
   * Works with both slideshow slides and direct cards.
   */
  #filterCards() {
    // Find all slideshow slides containing bag cards
    const slides = this.querySelectorAll('slideshow-slide');

    if (slides.length > 0) {
      // Working with slideshow structure
      for (const slide of slides) {
        const card = slide.querySelector('.bag-finder__card');
        if (!card) continue;

        const bagType = card.dataset.bagType?.toLowerCase();
        const shouldShow = this.#activeFilter === 'all' || bagType === this.#activeFilter;

        if (shouldShow) {
          slide.removeAttribute('hidden');
          slide.style.display = '';
        } else {
          slide.setAttribute('hidden', '');
          slide.style.display = 'none';
        }
      }

      // Scroll slideshow back to start when filter changes
      const scroller = this.querySelector('slideshow-slides');
      if (scroller) {
        scroller.scrollTo({ left: 0, behavior: 'smooth' });
      }
    } else if (this.refs.cards) {
      // Fallback to direct card filtering
      for (const card of this.refs.cards) {
        const bagType = card.dataset.bagType?.toLowerCase();
        const shouldShow = this.#activeFilter === 'all' || bagType === this.#activeFilter;

        if (shouldShow) {
          card.removeAttribute('hidden');
          card.style.display = '';
        } else {
          card.setAttribute('hidden', '');
          card.style.display = 'none';
        }
      }
    }
  }

  /**
   * Handles Klaviyo notify button clicks.
   *
   * @param {Event} event - The click event
   */
  #handleKlaviyoClick = (event) => {
    event.preventDefault();
    const target = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const formId = target.dataset.klaviyoForm;

    if (formId && window._klOnsite) {
      window._klOnsite.push(['openForm', formId]);
    } else if (formId) {
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', formId]);
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up filter button listeners
    if (this.refs.filterBtns) {
      for (const btn of this.refs.filterBtns) {
        btn.removeEventListener('click', this.#handleFilterClick);
      }
    }

    // Clean up Klaviyo button listeners
    const klaviyoBtns = this.querySelectorAll('.klaviyo-notify-btn');
    for (const btn of klaviyoBtns) {
      btn.removeEventListener('click', this.#handleKlaviyoClick);
    }
  }
}

// Register the custom element
if (!customElements.get('bag-finder-component')) {
  customElements.define('bag-finder-component', BagFinderComponent);
}
